import { useEffect, useState } from "react";

import { Loader, Table } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { EmptyMessage, Modal, Text } from "components";
import { DashboardService } from "services";
import {
  DashboardDailyStateItemResponse,
  DashboardDailyStateItemType,
  DashboardDailyStateResponse,
} from "types";

import shellClasses from "./SectionShell.module.css";
import classes from "./DailyStatusSection.module.css";

interface DailyStatusSectionProps {
  className?: string;
  selectedAgentIds?: number[] | null;
}

type DailyStatusMetricTone = "success" | "danger";

interface DailyStatusMetric {
  labelKey: string;
  value: number;
  tone: DailyStatusMetricTone;
  itemType: DashboardDailyStateItemType;
}

interface DailyStatusMetricDefinition {
  labelKey: string;
  tone: DailyStatusMetricTone;
  itemType: DashboardDailyStateItemType;
}

const DAILY_STATUS_METRICS: readonly DailyStatusMetricDefinition[] = [
  {
    labelKey: "dashboard.teamLead.dailyStatus.items.sentContracts",
    tone: "success",
    itemType: "SENT_CONTRACTS",
  },
  {
    labelKey: "dashboard.teamLead.dailyStatus.items.createdAdjustments",
    tone: "success",
    itemType: "ADJUSTMENTS",
  },
  {
    labelKey: "dashboard.teamLead.dailyStatus.items.incompleteDocumentation",
    tone: "danger",
    itemType: "INCOMPLETE_DOCUMENTATION",
  },
] as const;

const createDefaultMetrics = (): DailyStatusMetric[] =>
  DAILY_STATUS_METRICS.map((metric) => ({
    labelKey: metric.labelKey,
    value: 0,
    tone: metric.tone,
    itemType: metric.itemType,
  }));

const getNumberValue = (value: number | undefined) =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const mapDailyStateToMetrics = (data: DashboardDailyStateResponse): DailyStatusMetric[] => {
  const valuesByType: Record<DashboardDailyStateItemType, number> = {
    SENT_CONTRACTS: getNumberValue(data.sentContracts),
    ADJUSTMENTS: getNumberValue(data.createdAdjustments),
    INCOMPLETE_DOCUMENTATION: getNumberValue(data.incompleteDocumentation),
  };

  return DAILY_STATUS_METRICS.map((metric) => ({
    labelKey: metric.labelKey,
    value: valuesByType[metric.itemType],
    tone: metric.tone,
    itemType: metric.itemType,
  }));
};

const DailyStatusSection = ({ className, selectedAgentIds }: DailyStatusSectionProps) => {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState<DailyStatusMetric[]>(createDefaultMetrics());
  const [activeMetric, setActiveMetric] = useState<DailyStatusMetric | null>(null);
  const [details, setDetails] = useState<DashboardDailyStateItemResponse[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsLoadError, setDetailsLoadError] = useState(false);

  useEffect(() => {
    let mounted = true;
    const params = selectedAgentIds?.length
      ? { agentIds: selectedAgentIds }
      : undefined;

    const loadDailyState = async () => {
      try {
        const data = await DashboardService.getDailyState(params);

        if (!mounted) {
          return;
        }

        setMetrics(mapDailyStateToMetrics(data ?? {}));
      } catch {
        if (!mounted) {
          return;
        }

        setMetrics(mapDailyStateToMetrics({}));
      }
    };

    loadDailyState();

    return () => {
      mounted = false;
    };
  }, [selectedAgentIds]);

  useEffect(() => {
    if (!activeMetric) {
      return;
    }

    let mounted = true;
    const params = selectedAgentIds?.length
      ? { agentIds: selectedAgentIds }
      : undefined;

    const loadDailyStateItems = async () => {
      setDetailsLoading(true);
      setDetailsLoadError(false);
      setDetails([]);

      try {
        const data = await DashboardService.getDailyStateItems(activeMetric.itemType, params);

        if (!mounted) {
          return;
        }

        setDetails(data);
      } catch {
        if (!mounted) {
          return;
        }

        setDetailsLoadError(true);
      } finally {
        if (mounted) {
          setDetailsLoading(false);
        }
      }
    };

    loadDailyStateItems();

    return () => {
      mounted = false;
    };
  }, [activeMetric, selectedAgentIds]);

  const closeDetails = () => {
    setActiveMetric(null);
    setDetails([]);
    setDetailsLoadError(false);
  };

  return (
    <>
      <section className={[shellClasses.root, className].filter(Boolean).join(" ")}>
        <div className={shellClasses.header}>
          <Text className={shellClasses.title}>
            {t("dashboard.teamLead.dailyStatus.title")}
          </Text>
        </div>

        <div className={shellClasses.body}>
          <div className={classes.metrics}>
            <div className={classes.list}>
              {metrics.map((metric) => {
                const isDisabled = metric.value <= 0;

                return (
                  <button
                    type="button"
                    key={metric.labelKey}
                    disabled={isDisabled}
                    onClick={() => setActiveMetric(metric)}
                    aria-haspopup={isDisabled ? undefined : "dialog"}
                    aria-label={isDisabled ? undefined : t(metric.labelKey)}
                    className={[
                      classes.row,
                      isDisabled ? classes.rowDisabled : classes.rowInteractive,
                    ].join(" ")}
                  >
                    <Text className={classes.label}>{t(metric.labelKey)}</Text>

                    <span
                      className={[
                        classes.badge,
                        metric.tone === "danger" ? classes.danger : classes.success,
                      ].join(" ")}
                    >
                      {metric.value}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <Modal
        opened={!!activeMetric}
        onClose={closeDetails}
        title={activeMetric ? t(activeMetric.labelKey) : t("dashboard.teamLead.dailyStatus.title")}
        size="xl"
        styles={{
          header: {
            padding: "16px",
          },
          body: {
            padding: "0 8px 16px",
          },
        }}
      >
        {detailsLoading ? (
          <div className={classes.modalState}>
            <Loader size={32} type="oval" />
          </div>
        ) : detailsLoadError ? (
          <EmptyMessage message={t("dashboard.teamLead.dailyStatus.listLoadError")} marginTop={0} />
        ) : details.length === 0 ? (
          <EmptyMessage message={t("results.no")} marginTop={0} />
        ) : (
          <div className={classes.tableWrapper}>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("dashboard.teamLead.dailyStatus.columns.clientName")}</Table.Th>
                  <Table.Th>{t("dashboard.teamLead.dailyStatus.columns.requestStatus")}</Table.Th>
                  <Table.Th>{t("dashboard.teamLead.dailyStatus.columns.agentName")}</Table.Th>
                </Table.Tr>
              </Table.Thead>

              <Table.Tbody>
                {details.map((item, index) => (
                  <Table.Tr key={`${item.clientName}-${item.requestStatus}-${item.agentName ?? "-"}-${index}`}>
                    <Table.Td>{item.clientName}</Table.Td>
                    <Table.Td>
                      {t(`credit.status.name.${item.requestStatus}`, {
                        defaultValue: item.requestStatus,
                      })}
                    </Table.Td>
                    <Table.Td>{item.agentName ?? "-"}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}
      </Modal>
    </>
  );
};

export default DailyStatusSection;
